/**
 * useCategoryFilter — encapsulates the AIM-dashboard category-filter
 * state machine + lazy data fetching.
 *
 * Why a hook
 * ----------
 * The same state used to live inline in ``ResultsPage.js``, mixed with
 * audit-fetch, polling, download, and 15 other concerns — the file
 * grew past 1k lines. Extracting this surface lets us:
 *   • Unit-test the cache + reset semantics without booting the whole
 *     ResultsPage tree.
 *   • Reuse the pattern when (later) we add a per-AIM-element drill-down.
 *
 * Responsibilities
 * ----------------
 *   • Owns the selected category id (or null).
 *   • Lazily fetches every validation_result row for the selected
 *     category via ``GET /api/audits/{auditId}/validation-details``,
 *     paginated 2k rows at a time, with a 20k hard cap.
 *   • Caches results by category key so toggling is instant.
 *   • Resets cache + selection when ``auditId`` changes.
 *   • Computes the filtered chart view + the friendly label for the
 *     selected category.
 *   • Exposes ``setSelectedCategory`` for the breakdown rows + a
 *     ``clear`` helper used by the X button.
 *
 * Returns:
 *   {
 *     selectedCategory,        // string | null
 *     selectedCategoryLabel,   // friendly label or null
 *     setSelectedCategory,     // (string | null) => void  — clicked from a row
 *     clearSelection,          // () => void — X button
 *     detailsLoading,          // bool — actively fetching rows
 *     detailsError,            // string — last fetch error
 *     detailsTruncated,        // bool — hit the 20k hard cap
 *     filteredView,            // { data, summary } | null — chart inputs
 *     chartData,               // ALWAYS-defined chart input (baseData when no filter)
 *     chartSummary,            // ALWAYS-defined summary (audit.summary when no filter)
 *   }
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { apiFetch } from './supabase';
import { deriveCategoryFilteredView } from './categoryFilter';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Paging knobs — kept here (not exposed as props) so the network
// behaviour is uniform across every consumer of the hook.
const PAGE = 2000;
const HARD_CAP = 20000;

export function useCategoryFilter({ auditId, audit, baseData }) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [detailsByCategory, setDetailsByCategory] = useState({});
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [detailsTruncated, setDetailsTruncated] = useState(false);

  // Lazy fetch — only runs when a category is selected AND we haven't
  // cached its rows yet. Cached results survive the lifetime of the
  // ResultsPage instance.
  useEffect(() => {
    if (!auditId || !selectedCategory) return;
    if (detailsByCategory[selectedCategory]) return; // cache hit

    let cancelled = false;
    const load = async () => {
      setDetailsLoading(true);
      setDetailsError('');
      setDetailsTruncated(false);
      const all = [];
      try {
        let offset = 0;
        while (offset < HARD_CAP) {
          const url = `${API_URL}/api/audits/${auditId}/validation-details`
            + `?category=${encodeURIComponent(selectedCategory)}`
            + `&limit=${PAGE}&offset=${offset}`;
          const resp = await apiFetch(url);
          if (!resp.ok) {
            const t = await resp.text().catch(() => '');
            throw new Error(`Validation details failed (${resp.status}): ${t.slice(0, 160)}`);
          }
          const json = await resp.json();
          const rows = json.results || [];
          all.push(...rows);
          if (cancelled) return;
          if (!json.has_more || rows.length < PAGE) break;
          offset += PAGE;
        }
        if (!cancelled) {
          // Functional setState so concurrent category clicks don't
          // clobber each other's results.
          setDetailsByCategory((prev) => ({ ...prev, [selectedCategory]: all }));
          setDetailsTruncated(all.length >= HARD_CAP);
        }
      } catch (e) {
        if (!cancelled) setDetailsError(e.message || 'Failed to load category details');
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [auditId, selectedCategory, detailsByCategory]);

  // Reset everything when the audit changes — distinct audits have
  // distinct validation_results sets, so the cache must drop.
  useEffect(() => {
    setSelectedCategory(null);
    setDetailsByCategory({});
    setDetailsError('');
    setDetailsTruncated(false);
  }, [auditId]);

  const clearSelection = useCallback(() => setSelectedCategory(null), []);

  const summary = audit?.summary || {};
  const filteredView = useMemo(() => {
    if (!selectedCategory) return null;
    const rows = detailsByCategory[selectedCategory];
    if (!rows) return null; // still fetching — caller falls back to baseData
    return deriveCategoryFilteredView(baseData, rows, selectedCategory);
  }, [baseData, detailsByCategory, selectedCategory]);

  const chartData = filteredView ? filteredView.data : baseData;
  const chartSummary = filteredView ? filteredView.summary : summary;

  const selectedCategoryLabel = useMemo(() => {
    if (!selectedCategory) return null;
    const cb = audit?.category_breakdown || [];
    return (cb.find((c) => c.category === selectedCategory)?.label) || selectedCategory;
  }, [selectedCategory, audit]);

  return {
    selectedCategory,
    selectedCategoryLabel,
    setSelectedCategory,
    clearSelection,
    detailsLoading,
    detailsError,
    detailsTruncated,
    filteredView,
    chartData,
    chartSummary,
  };
}
